// Shared name validation logic used by both client and server
export const validateName = (name: string): { isValid: boolean; error?: string } => {
    const trimmed = name.trim();

    if (trimmed.length === 0) {
        return { isValid: false, error: 'Name is required' };
    }

    if (trimmed.length > 50) {
        return { isValid: false, error: 'Name must be 50 characters or less' };
    }

    // Single comprehensive check for all dangerous content
    if (/<|>|&lt;|&gt;|&#|javascript:|data:|vbscript:|https?:\/\/|www\./i.test(trimmed)) {
        return { isValid: false, error: 'Names cannot contain HTML, scripts, or URLs' };
    }

    return { isValid: true };
};

// Simple sanitization - remove dangerous content
export const sanitizeName = (name: string): string => {
    let sanitized = name;
    
    // Remove script/style tags with content, then other HTML tags and dangerous content
    sanitized = sanitized
        .replace(/<script[^>]*>.*?<\/script>/gi, ' ')
        .replace(/<style[^>]*>.*?<\/style>/gi, ' ')
        .replace(/<[^>]*>|javascript:[^\s]*|data:[^\s]*|vbscript:[^\s]*|https?:\/\/[^\s]+|www\.[^\s]+/gi, ' ');
    
    // Clean up multiple spaces and trim
    sanitized = sanitized.replace(/\s+/g, ' ').trim();
    
    return sanitized;
};

// HTML escape for email templates - critical for security
export const escapeHtmlForEmail = (str: string): string => {
    const htmlEscapes: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
    };
    
    return str.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
};

// Helper to validate and sanitize both names at once
export const validateAndSanitizeNames = (firstName: string, lastName: string): { firstName: string; lastName: string } => {
    const firstNameValidation = validateName(firstName);
    const lastNameValidation = validateName(lastName);
    
    if (!firstNameValidation.isValid) {
        throw new Error(firstNameValidation.error);
    }
    if (!lastNameValidation.isValid) {
        throw new Error(lastNameValidation.error);
    }
    
    return {
        firstName: sanitizeName(firstName),
        lastName: sanitizeName(lastName)
    };
};